<template>
    <div class="ovt__objects-table">
        <div class="ovt__objects-table__box">
            <button class="link ovt__objects-table__button" @click="configureObjects()">
                <i class="icon-settings" /><span>{{
                    $t.configureYourOwnListOfObjects
                }}</span>
            </button>
        </div>
        <div v-if="finalAsteroidsList.length" class="ovt__objects-table__content" :class="{ loading }">
            <div class="d-table-row">
                <div class="d-table-cell">{{ $t.object }}</div>
                <div class="d-table-cell-empty"></div>
                <div class="d-table-cell orbit-switcher label">
                    {{ $t.orbitSwitchLabel }}
                    <Tooltip position="left" :tooltipText="$keys.t.switchOrbit"><i class="icon-info" /></Tooltip>
                </div>
                <div class="d-table-cell">{{ $t.show }}</div>
            </div>
            <div class="inner">
                <div class="d-table-row item" v-for="(item, index) of finalAsteroidsList" :key="index"
                    :class="{ 'item-highlight': isSelectedObject(item) }">
                    <div class="d-table-cell">
                        <input type="radio" :disabled="getItemDisabled(item)" :id="'radio_' + item.id" :value="item.id" name="radioGroup"
                            :checked="isSelectedObject(item)" @change="handleSelection(item)" />
                    </div>
                    <div class="d-table-cell">
                        <span @click="handleSelection(item)" :style="{ color: getItemColor(item) }">
                            {{ displayName(item) }}
                        </span>
                        <div class="synthetic-objects__buttons">
                          <button
                              class="synthetic-objects__button synthetic-objects__button--remove tertiary"
                              @click="handleObjectRemove(item)"
                          >
                            {{ $t.remove }}
                          </button>
                        </div>
                    </div>
                    <div class="d-table-cell orbit-switcher field">
                        <input class="toggle-switch" :id="`perturbedOrbit_${item.id}`" :checked="isPerturbedOn(item)"
                            :disabled="getItemDisabled(item, 'toggle-switch')" type="checkbox" @click="
                                orbitSwitchClick(
                                    item,
                                    $event.target.checked,
                                    $event
                                )
                                " />
                        <label class="toggle-switch__button" :class="{disabled: getItemDisabled(item, 'toggle-switch')}"
                            :for="`perturbedOrbit_${item.id}`"></label>
                    </div>
                    <div class="d-table-cell input">
                        <input type="checkbox"  :id="item.name" :checked="isShowChecked(item)"
                            @change="handleObjectVisibility(item, $event.target.checked)" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Tooltip from "@/components/common/Tooltip";
import config from "@/constants/config";
import ObjectsService from "@/services/objects.service";
import PopupService from "@/services/popup.service";
import SynodicCalculationService from "@/services/synodic-calculation.service";
import UtilsService from "@/services/utils.service";
import VisualisationService from "@/services/visualisation.service";
import Config from "@/constants/config";

export default {
    name: "SynodicOrbitObjectsList",
    data() {
        return {
            showCheckedByDesignator: {},
        };
    },
    components: {
        Tooltip,
    },
    computed: {
        finalAsteroidsList() {
            return ObjectsService.getFinalAsteroidsList();
        },

        visibleRealSynodicObjectsList() {
            return SynodicCalculationService.getVisibleRealSynodicObjectsList();
        },

        selectedSynodicObjectName() {
            return VisualisationService.getSelectedSynodicObjectName();
        },

        loading() {
            return VisualisationService.loadingObjectsEphemerides().length;
        },
    },
    methods: {
        getItemDisabled(item, type) {
            if (((item.code === "non-NEO" || item.keplerianOnly) && type === 'toggle-switch')  || (item.sourceType.name === "NEOCP")) {
                return true;
            } else
                return false;
        },
        getItemColor(item) {
            const settings = config.visualisation.settings;
            if (item.code === "non-NEO") {
                return settings.nonNEAcolor;
            } else if (item.sourceType.name === "NEOCP"){
                return settings.NEOCPcolor;
            }
        },
        displayName(item) {
            return ObjectsService.objectNumberAndName(item);
        },

        configureObjects() {
            PopupService.show({
                component: "PopupObjectsConfiguration",
            });
        },

        handleObjectRemove(object) {
            PopupService.show({
              component: "PopupSyntheticObjectDelete",
              selectedObject: object,
              removeByObject: Config.removeByObject.database,
            });
        },

        handleSelection(item) {
            if (item.sourceType.name !== "NEOCP"){
                const designator = this.getDesignator(item);
                VisualisationService.selectSynodicObject(designator);
            }
        },

        isSelectedObject(item) {
            return this.getDesignator(item) === this.selectedSynodicObjectName;
        },

        async handleObjectVisibility(item, isChecked) {
            const designator = this.getDesignator(item);
            this.showCheckedByDesignator[designator] = isChecked;
            if (isChecked !== this.isObjectVisible(item)) {
                await SynodicCalculationService.handleObjectVisibility(designator);
            }
        },

        isShowChecked(item) {
            const designator = this.getDesignator(item);
            const localValue = this.showCheckedByDesignator[designator];
            if (typeof localValue === "boolean") {
                return localValue;
            }
            return this.isObjectVisible(item);
        },

        syncShowCheckedByDesignator() {
            const visibilityMap = {};
            this.finalAsteroidsList.forEach((item) => {
                const designator = this.getDesignator(item);
                visibilityMap[designator] = this.visibleRealSynodicObjectsList.includes(designator);
            });
            this.showCheckedByDesignator = visibilityMap;
        },

        isObjectVisible(item) {
            const designator = this.getDesignator(item);
            return this.visibleRealSynodicObjectsList.includes(designator);
        },

        isObjectCalculated(item) {
            const designator = this.getDesignator(item);
            const calculatedObjectList = SynodicCalculationService.getCalculatedRealSynodicObjectsList();
            return !!UtilsService.findItemInObjectList(
                "designator",
                designator,
                calculatedObjectList
            );
        },
        async orbitSwitchClick(item, isChecked, event) { /* eslint-disable-line */
            let isPerturbedAvailable = true;
            const designator = this.getDesignator(item);
            const isVisible = SynodicCalculationService.getVisibleRealSynodicObjectsList().includes(
                designator
            );
            if (isChecked && isVisible) {
                //event.preventDefault(); - this was blocking the switcher so I decided to comment it
            }
            if (!isChecked && isVisible) {
                SynodicCalculationService.switchOrbitToKeplerian(designator);
            }
            if (isChecked && isVisible) {
                isPerturbedAvailable = await SynodicCalculationService.switchOrbitToPerturbed(
                    designator, item.type
                );
            }
            if (isChecked && !isVisible && (this.isSelectedObject(item) || this.isShowChecked(item))) {
                try {
                    isPerturbedAvailable = await SynodicCalculationService.switchOrbitToPerturbed(
                        designator, item.type
                    );
                } catch (error) {
                    isPerturbedAvailable = false;
                }
            }
            isVisible &&
                SynodicCalculationService.checkIsFocusOnObject(designator);
            const perturbedOrbitList = UtilsService.deepCopy(
                SynodicCalculationService.getPerturbedSynodicOrbitList()
            );
            if (isChecked && isPerturbedAvailable) {
                perturbedOrbitList.push(designator);
            }
            if (!isChecked || !isPerturbedAvailable) {
                const objectIndex = perturbedOrbitList.indexOf(designator);
                objectIndex !== -1 && perturbedOrbitList.splice(objectIndex, 1);
            }
            await SynodicCalculationService.setPerturbedSynodicOrbitList(
                perturbedOrbitList
            );
            if (!isPerturbedAvailable) {
                SynodicCalculationService.showNoPerturbedInfo(designator);
                event.target.checked = false;
                item.perturbedOn = isPerturbedAvailable
                item.keplerianOnly = !isPerturbedAvailable
            }
        },

        isPerturbedOn(item) {
            const designator = this.getDesignator(item);
            return SynodicCalculationService.getPerturbedSynodicOrbitList().includes(
                designator
            );
        },

        getDesignator(object) {
            return (object.number ? object.number : object.name).toString();
        },
    },
    watch: {
        visibleRealSynodicObjectsList: {
            deep: true,
            handler() {
                this.syncShowCheckedByDesignator();
            },
        },
        finalAsteroidsList: {
            deep: true,
            handler(newVal) {
                this.syncShowCheckedByDesignator();
                SynodicCalculationService.verifyCalculatedRealSynodicObjectsList(
                    newVal,
                    this.selectedSynodicObjectName
                );
            },
        },
    },
    mounted() {
        ObjectsService.setUserDefinedObjects(true);
        this.syncShowCheckedByDesignator();

        const queryDesignator = this.$route.query.object;
        if (queryDesignator) {
            ObjectsService.findAsteroidByPhrase(queryDesignator, true, 'sovt')
        }
    },
};
</script>

<style>
.d-table-cell-empty {
    width: 50px;
}
</style>
